import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const MARKET_BY_REGION = {
  "Cape Town": { country: "ZA", locale: "en-ZA", currency: "ZAR", timeZone: "Africa/Johannesburg" },
  Johannesburg: { country: "ZA", locale: "en-ZA", currency: "ZAR", timeZone: "Africa/Johannesburg" },
  Berlin: { country: "DE", locale: "de-DE", currency: "EUR", timeZone: "Europe/Berlin" },
  Oslo: { country: "NO", locale: "nb-NO", currency: "NOK", timeZone: "Europe/Oslo" },
  Lisbon: { country: "PT", locale: "pt-PT", currency: "EUR", timeZone: "Europe/Lisbon" },
  Tokyo: { country: "JP", locale: "ja-JP", currency: "JPY", timeZone: "Asia/Tokyo" },
};

const FALLBACK_MARKET = MARKET_BY_REGION["Cape Town"];
const LocaleContext = createContext(null);

const MESSAGES = {
  en: {
    signupRetry: "Unable to sign up. Please try again later.",
    signupPreparing: "Preparing signup…",
    signupWebsite: "Website",
    eventDateAll: "All",
    eventDateToday: "Today",
    eventDateWeekend: "Weekend",
    eventDateMonth: "Month",
    headerSlogan: "Connecting artists to every Joe",
    developedBy: "Developed by",
    appVersion: "Version {version}",
    legalTerms: "Terms of Service",
    legalPrivacy: "Privacy Policy",
    loadingContent: "Loading content",
    homeSearchPlaceholder: "Search events, artists, venues, studios, media",
    homeMediaTab: "Media",
    homeMediaTitle: "Media",
    homeMediaSubtitle: "Find Media for live shows, music videos, and promotional content",
    studioRateFromPerHour: "From {price}/hr",
    studioRateOnRequest: "Contact for rates",
    feeUnavailable: "Fee unavailable",
    priceRangeFrom: "From {amount}",
    priceRangeUpTo: "Up to {amount}",
    artistBannerAlt: "{name} banner",
    profileMedia: "Media",
    profileGallery: "Gallery",
    profileGalleryImageAlt: "Gallery image {number}",
    profileNoMedia: "No media yet.",
    profileSimilarStudios: "Similar studios",
    profileSimilarMedia: "Similar media",
    profileViewStudio: "View studio",
    profileViewMedia: "View media profile",
    profileStudioHeroAlt: "{name} studio",
    profileMediaHeroAlt: "{name} media profile",
    profileRatingLabel: "Member rating",
    profileAccountLinked: "Account linked",
    profileRatingReviewOne: "{count} review",
    profileRatingReviewOther: "{count} reviews",
    profileRatingAccessible: "Member rating {rating} out of 5, {count} reviews",
    profileRatingAccessibleNoCount: "Member rating {rating} out of 5",
    loginModalTitle: "Sign in to continue",
    loginModalDefaultReason: "Sign in to use this feature.",
    loginModalMessageReason: "Sign in to start a conversation.",
    loginModalFollowReason: "Sign in to follow this page.",
    follow: "Follow",
    unfollow: "Unfollow",
    followerCountOne: "{count} follower",
    followerCountOther: "{count} followers",
    managedPageViewFollowers: "View followers",
    managedPageFollowersProRequired: "Upgrade to Pro to see who follows this page.",
    managedPageFollowerAnonymous: "Band-Joe member",
    managedPageFollowersEmpty: "No followers yet.",
    managedPageFollowersError: "Followers could not be loaded. Please try again.",
    followAdded: "Added to your following list.",
    followRemoved: "Removed from your following list.",
    followError: "Your following list could not be updated. Please try again.",
    followingTitle: "Following",
    followingDescription: "Artists, venues, studios, and Media you follow.",
    followingEmpty: "You are not following any pages yet.",
    followTypeartist: "Artist",
    followTypevenue: "Venue",
    followTypestudio: "Studio",
    followTypemedia: "Media",
    loginModalInviteReason: "Sign in to send an invitation.",
    loginModalReviewReason: "Sign in to leave a review.",
    reviewArtistTitle: "Rate this artist",
    reviewVenueTitle: "Rate this venue",
    reviewNoEligibleEvent: "You can review after a shared event has ended. There are no eligible events to review.",
    reviewEventLabel: "Completed event",
    reviewCategoryprofessionalism: "Professionalism",
    reviewCategorypunctuality: "Punctuality",
    reviewCategorysound: "Sound",
    reviewCategorycrowd: "Crowd experience",
    reviewTextLabel: "Your review",
    reviewCancel: "Cancel",
    reviewSubmit: "Publish review",
    reviewSubmitting: "Publishing…",
    reviewSuccess: "Your review has been published.",
    eventRateArtist: "Rate",
    authEmail: "Email",
    authPassword: "Password",
    authTogglePassword: "Show or hide password",
    authForgotPassword: "Forgot password?",
    authSignIn: "Sign in",
    authLogout: "Log out",
    inbox: "Inbox",
    profile: "Profile",
    accountRoleARTIST: "Artist",
    accountRoleGUEST: "Guest",
    accountRoleVENUE: "Venue",
    accountRoleSTUDIO: "Studio",
    accountRoleMEDIA: "Media",
    accountRoleADMIN: "Administrator",
    signupGuestDescription: "Follow artists, venues, and studios without creating or managing a page.",
    authSigningIn: "Signing in…",
    authNewHere: "New here?",
    authCreateAccount: "Create an account",
    authBrowseWithoutSigningIn: "Browse without signing in",
    authSignInFailed: "Failed to sign in. Please try again.",
    activationTitle: "Activate your account",
    activationWorking: "Activating your account…",
    activationSuccess: "Your account is active and you are signed in.",
    activationInvalid: "This activation link is invalid or has expired.",
    activationContinue: "Continue to your profile",
    activationPending: "Account created. Check your email and open the activation link before signing in.",
    profileArtistPagesTitle: "Your artist profiles",
    profileArtistPagesDescription: "Set up and manage the artist profiles you own.",
    profileCreateArtistPage: "Create artist profile",
    profileVenuePagesTitle: "Your venue profiles",
    profileVenuePagesDescription: "Set up and manage the venue profiles you own.",
    profileCreateVenuePage: "Create venue profile",
    profileStudioPagesTitle: "Your studio profiles",
    profileStudioPagesDescription: "Set up and manage the studio profiles you own.",
    profileCreateStudioPage: "Create studio profile",
    profileMediaPagesTitle: "Your media profiles",
    profileMediaPagesDescription: "Set up and manage the media profiles you own.",
    profileCreateMediaPage: "Create media profile",
    profileProLimitSummary: "Pro accounts can create up to {count} profiles.",
    profileFreeLimitSummary: "Free accounts can create one profile. Upgrade to Pro to create up to 5.",
    profileProLimitReached: "You have reached the Pro limit of {count} profiles.",
    profileFreeLimitReached: "You need a Pro account to create more than one profile.",
    createPageKindArtist: "artist profile",
    createPageKindVenue: "venue profile",
    createPageKindStudio: "studio profile",
    createPageKindMedia: "media profile",
    createPageTitle: "Create {type}",
    createPageEyebrow: "New managed profile",
    createPageName: "Display name",
    createPageCity: "City",
    createPageAbout: "About",
    createPageOnboarding: "Your account is ready. Complete this draft so people can discover and understand your page.",
    createPageMedia: "Images",
    createPageProfileImage: "Profile image",
    createPageCoverImage: "Cover image",
    createPageGallery: "Gallery images",
    imageInputHint: "Upload an image or enter an HTTP(S) URL. Uploads are resized to fit within 800 × 800 px on a white background.",
    imageInputUpload: "Upload",
    imageInputAddUrl: "Add URL",
    imageInputInvalid: "Enter a valid HTTP or HTTPS image URL.",
    imageInputInvalidFile: "Choose a valid PNG, JPEG, or WebP image.",
    imageInputPreview: "Image preview {number}",
    imageInputRemove: "Remove image {number}",
    createPageTagEntryHint: "Type a value and press Enter.",
    createPageContact: "Contact and links",
    createPagePhone: "Public phone number",
    createPageWebsite: "Website URL",
    createPageWebsiteHint: "You can enter a full URL or a domain such as bandname.com.",
    createPageWebsiteInvalid: "Enter a valid website address using HTTP or HTTPS.",
    createPageSocialTitle: "Social media links",
    createPageSocialHint: "Add the public profiles shown on your artist page.",
    createPageSocialInvalid: "Enter valid social media addresses using HTTP or HTTPS.",
    createPageSocialSpotify: "Spotify URL",
    createPageSocialYoutube: "YouTube URL",
    createPageSocialInstagram: "Instagram URL",
    createPageSocialFacebook: "Facebook URL",
    createPageSocialSoundcloud: "SoundCloud URL",
    createPageArtistDetails: "Artist details",
    createPageVenueDetails: "Venue details",
    createPageStudioDetails: "Studio details",
    createPageMediaDetails: "Media details",
    createPageGenres: "Genres",
    createPageMembers: "Members",
    createPageTravelDistance: "Travel distance (km)",
    createPageFeeMin: "Minimum fee",
    createPageFeeMax: "Maximum fee",
    createPageAddress: "Address",
    createPageCapacity: "Capacity",
    createPageBudgetMin: "Minimum budget",
    createPageBudgetMax: "Maximum budget",
    createPageFacilities: "Facilities",
    createPageStageSpec: "Stage specification",
    createPagePolicies: "Booking policies",
    createPageServices: "Services",
    createPageRooms: "Rooms",
    createPageGear: "Gear",
    createPageCurrency: "Currency (ISO 4217)",
    createPageCurrencyInvalid: "Enter a three-letter ISO 4217 currency code.",
    createPageTimeZone: "Studio timezone",
    createPageTimeZoneHint: "IANA name, for example Africa/Johannesburg",
    createPageTimeZoneInvalid: "Enter a valid IANA timezone.",
    createPageAddRoom: "Add room",
    createPageRoomsEmpty: "No rooms added yet.",
    createPageRoomName: "Room name",
    createPageRoomSize: "Room size or capacity",
    createPageRoomPhoto: "Room photo",
    createPageEngineers: "Staff",
    createPageAddEngineer: "Add staff",
    createPageEngineersEmpty: "No staff added yet.",
    createPageEngineerName: "Staff name",
    createPageEngineerRole: "Role or speciality",
    createPageEngineerAvatar: "Profile image",
    createPageEngineerInstagram: "Instagram URL",
    createPageRates: "Rates",
    createPageAddRate: "Add rate",
    createPageRatesEmpty: "No rates added yet.",
    createPageRateLabel: "Rate name and unit",
    createPageRatePrice: "Price",
    createPagePortfolio: "Portfolio",
    createPageAddPortfolio: "Add portfolio item",
    createPagePortfolioEmpty: "No portfolio items added yet.",
    createPagePortfolioType: "Media type",
    createPagePortfolioAudio: "Audio",
    createPagePortfolioVideo: "Video",
    createPagePortfolioTitle: "Title",
    createPagePortfolioUrl: "Media URL",
    createPagePortfolioThumb: "Cover image",
    createPageRemoveItem: "Remove",
    availabilityTitle: "Availability",
    availabilityEditorTitle: "Publish availability",
    availabilityEditorHint: "Add the dates people can book you. Calendar dates remain tied to the listed IANA timezone.",
    availabilityStartDate: "Start date",
    availabilityEndDate: "End date",
    availabilityState: "Status",
    availabilityStateAVAILABLE: "Available",
    availabilityStateTENTATIVE: "Tentative",
    availabilityStateUNAVAILABLE: "Unavailable",
    availabilityTimeZone: "Timezone",
    availabilityTimeZoneHint: "IANA name, for example Africa/Johannesburg",
    availabilityNotes: "Public notes (optional)",
    availabilityAdd: "Add dates",
    availabilityRemove: "Remove availability",
    availabilityEmpty: "No availability published.",
    availabilityInvalid: "Check that every availability range has valid dates, a status, and an IANA timezone.",
    createPageSave: "Save draft",
    createPageSaving: "Saving",
    createPageCancel: "Cancel",
    editPageTitle: "Edit {type}",
    editPageEyebrow: "Managed profile",
    editPageSave: "Save changes",
    editPageNotOwned: "This page is not available in your managed profiles.",
    editPageLocationTitle: "Fine-tune location",
    editPageLocationHint: "Click the map or drag the marker to set the precise public location.",
    editPageLocationMapLabel: "Fine-tune profile location",
    editPageLatitude: "Latitude",
    editPageLongitude: "Longitude",
    editPageLocationInvalid: "Enter valid latitude and longitude coordinates.",
    managedPageOpen: "Open",
    managedPageEdit: "Edit",
    managedPageDelete: "Delete",
    managedPageDeleteTitle: "Delete profile page?",
    managedPageDeleteConfirmation: "Delete “{name}” permanently? This removes the page and its related follows, reviews, conversations, and open calls. This cannot be undone.",
    managedPageDeleteCancel: "Keep page",
    managedPageDeleteConfirm: "Delete page",
    managedPageDeleting: "Deleting…",
    managedPageDeleteSuccess: "Profile page deleted.",
    managedPageDeleteError: "The profile page could not be deleted. Please try again.",
    artistProfileEdit: "Edit profile",
    artistProfileMembers: "Members",
    artistUpcomingEvents: "Upcoming events",
    artistNoUpcomingEvents: "No upcoming events.",
    artistPastEvents: "Past events",
    artistNoPastEvents: "No past events yet.",
    venueEventsPostGig: "Post a gig",
    venueUpcomingEvents: "Upcoming events",
    venueNoUpcomingEvents: "No upcoming events.",
    venuePastEvents: "Past events",
    venueNoPastEvents: "No past events yet.",
    venueEventCompleted: "Completed",
    venueSetAvailability: "Set availability",
    venueManageAvailability: "Manage availability",
    gigPublishSuccess: "Gig published to the board and event created.",
    gigPublishIncomplete: "The gig could not be published because its event was not created. Please try again.",
    gigEditTitle: "Edit gig and event",
    gigEditHeading: "Update the opportunity",
    gigCreateHeading: "Find the right act",
    gigVenueWorkspace: "Venue workspace",
    gigBriefTitle: "A clear brief gets better applications",
    gigBriefHint: "Include the slot, load-in, available backline, payment timing, and a realistic deadline.",
    gigTitle: "Gig title",
    gigDescription: "Event and gig description",
    eventBannerUrlLabel: "Banner image (optional)",
    eventBannerUrlHint: "Upload or link to the image shown as the event banner.",
    eventBannerUrlInvalid: "Upload an image or enter a valid HTTP or HTTPS banner URL.",
    eventParkingLabel: "Parking details (optional)",
    eventAccessibilityLabel: "Accessibility features (optional)",
    eventAccessibilityHint: "Add each feature separately.",
    eventArtistInviteLabel: "Invite artists (optional)",
    eventArtistInviteHint: "Search for one or more artists. They will be invited when the event is created.",
    eventArtistInviteEmpty: "No artists found",
    eventArtistInviteLoadError: "Artists could not be loaded. You can still create the event without invitations.",
    eventCapacityLabel: "Event capacity",
    eventCapacityInvalid: "Enter an event capacity of at least 1.",
    eventCapacityExceedsVenue: "Event capacity cannot exceed the venue capacity of {capacity}.",
    eventCapacityVenueMaximum: "Maximum for this venue: {capacity}.",
    eventCapacityVenueUnknown: "The venue must have a capacity before an event can be saved.",
    gigPerformanceStart: "Performance date and time",
    gigApplicationDeadline: "Application deadline",
    gigDoorsAt: "Doors open (optional)",
    gigEndAt: "Performance end (optional)",
    gigGenres: "Genres",
    gigBudgetMin: "Minimum budget",
    gigBudgetMax: "Maximum budget",
    gigCurrency: "Currency",
    gigSetLength: "Set length (minutes)",
    gigBackline: "Backline provided",
    gigEquipment: "Equipment requirements",
    gigPaymentTerms: "Payment and engagement terms",
    gigAgeLimit: "Age limit (optional)",
    gigAttachments: "Add attachments (optional)",
    gigBudgetInvalid: "Minimum budget must not exceed maximum budget.",
    gigDeadlineInvalid: "Application deadline must be before the performance.",
    eventStartInvalid: "Enter a valid performance start date and time.",
    eventEndInvalid: "Enter a valid performance end date and time.",
    eventEndBeforeStart: "Performance end must be after the performance start.",
    eventDoorsInvalid: "Enter a valid doors-open date and time.",
    eventDoorsAfterStart: "Doors must open at or before the performance starts.",
    eventDeadlineInvalid: "Enter a valid application deadline.",
    gigAttachmentInvalid: "Attachments must be PDF, PNG, or JPEG files no larger than 10 MB.",
    gigSaving: "Saving…",
    gigSaveChanges: "Save changes",
    gigUpdateSuccess: "Gig and event updated.",
    eventEdit: "Edit event",
    venueInviteArtists: "Invite artists",
    eventBuyTickets: "Buy Tickets",
    eventViewOnFacebook: "View on Facebook",
    eventGetTickets: "Get Tickets",
    eventPriceFree: "Free",
    eventPriceUnavailable: "Price unavailable",
    cityFieldLabel: "City",
    cityFieldPrompt: "Select a city",
    cityFieldLoading: "Loading citiesâ€¦",
    cityFieldError: "Cities could not be loaded. Please try again.",
    cityFieldSearchWorldwide: "Search worldwide",
    cityFieldAttribution: "Location data Â© OpenStreetMap contributors",
    discoveryMapLabel: "Event locations",
    artistMapLabel: "Artist locations",
    venueMapLabel: "Venue locations",
    studioMapLabel: "Studio locations",
    mediaMapLabel: "Media locations",
    gigMapLabel: "Gig locations",
    filters: "Filters",
    listView: "List",
    mapView: "Map",
    discoveryMapEmpty: "No precise locations are available for these results.",
    close: "Close",
    inboxConversationError: "The conversation could not be opened. Please try again.",
    inboxMessagesError: "Messages could not be loaded. Please try again.",
    inboxSearch: "Search conversations",
    inboxNoMessages: "No messages yet",
    inboxOrganisationConversation: "Organisation conversation",
    inboxWriteMessage: "Write a message",
    inboxSendMessage: "Send message",
    inboxRefreshMessages: "Refresh messages",
    inboxBackToConversations: "Back to conversations",
    inboxRecipientCannotReply: "This recipient does not have a Pro account, so they will not be able to reply here. Include your contact details in your message so they can respond another way.",
    inboxEventInvitationTitle: "Event invitation",
    inboxEventInvitationAccept: "Accept",
    inboxEventInvitationDecline: "Decline",
    inboxEventInvitationAccepted: "Invitation accepted",
    inboxEventInvitationDeclined: "Invitation declined",
    inboxEventInvitationResponseError: "The invitation response could not be saved. Please try again.",
    proAccountRequired: "You need a Pro account to use this feature.",
    proFeaturesTitle: "Pro features",
    proFeaturesDescription: "Upgrade to Pro to unlock these tools.",
    proUpgrade: "Upgrade to Pro",
    profileGetProTitle: "Get Pro",
    profileGetProDescription: "Join the mailing list for Pro launch updates and early access news.",
    profileGetProAction: "Get Pro",
    proWaitlistTitle: "Join the Pro waitlist?",
    proWaitlistConfirmation: "We’ll add {email} to the mailing list for Pro launch updates. Would you like to join?",
    proWaitlistCancel: "Not now",
    proWaitlistConfirm: "Join waitlist",
    proWaitlistJoining: "Joining…",
    proWaitlistSuccess: "You’re on the Pro waitlist.",
    proWaitlistAlreadyJoined: "You’re already on the Pro waitlist.",
    proWaitlistError: "We couldn’t add you to the Pro waitlist. Please try again.",
    proUploadTechRider: "Upload tech rider",
    techRiderLabel: "Technical rider",
    techRiderHint: "Upload one PDF up to 10 MB. It will be available from your artist profile.",
    techRiderProHint: "Technical-rider uploads are available with an Artist Pro account.",
    techRiderReplace: "Replace technical rider",
    techRiderInvalid: "Choose a PDF file no larger than 10 MB.",
    techRiderReadError: "The technical rider could not be read. Please try again.",
    techRiderPdfAvailable: "PDF available",
    techRiderNotAvailable: "Not available",
    techRiderDownload: "Download",
    technicalDocumentDownloadError: "The document could not be downloaded. Refresh the page and try again.",
    stageLayoutLabel: "Stage layout",
    stageLayoutDownload: "Download stage layout",
    proMultipleArtistPages: "Create another artist page",
    proUploadStagePlot: "Upload stage plot",
    proMultipleVenuePages: "Create another venue page",
    proUploadStudioLayout: "Upload studio layout",
    proMultipleStudioPages: "Create another studio page",
    proUploadInboxFiles: "Upload files",
    proNotifications: "Notifications",
    gigApply: "Apply",
    venueContact: "Contact",
    studioContact: "Contact",
    studioRequestSession: "Request a session",
    eventTicketProviderLabel: "Ticket sale link",
    eventTicketProviderHint: "Link to the event page on your external ticket provider.",
    eventTicketProviderProHint: "A Pro account is required to add an external ticket sale link.",
    notificationEmailMessages: "Email me for new messages",
    notificationEmailApplications: "Email me when I receive applications or invitations",
    notificationEmailReviews: "Email me when I receive reviews",
    notificationPushMessages: "Allow push notifications on mobile",
    notificationSave: "Save notifications",
  },
  de: {
    signupRetry: "Die Registrierung ist derzeit nicht möglich. Bitte versuche es später erneut.",
    signupPreparing: "Registrierung wird vorbereitet…",
    signupWebsite: "Website",
    eventDateAll: "Alle",
    eventDateToday: "Heute",
    eventDateWeekend: "Wochenende",
    eventDateMonth: "Monat",
    headerSlogan: "Bands mit jedem Joe verbinden",
    homeSearchPlaceholder: "Veranstaltungen, Künstler, Veranstaltungsorte, Studios und Medien suchen",
    homeMediaTab: "Medien",
    homeMediaTitle: "Medien",
    homeMediaSubtitle: "Finde Medien für Live-Auftritte, Musikvideos und Werbeinhalte",
    studioRateFromPerHour: "Ab {price}/Std.",
    studioRateOnRequest: "Preis auf Anfrage",
    feeUnavailable: "Honorar nicht verfügbar",
    priceRangeFrom: "Ab {amount}",
    priceRangeUpTo: "Bis {amount}",
    stageLayoutLabel: "Bühnenplan",
    stageLayoutDownload: "Bühnenplan herunterladen",
    artistBannerAlt: "Banner von {name}",
    developedBy: "Entwickelt von",
    appVersion: "Version {version}",
    proUpgrade: "Auf Pro upgraden",
    proWaitlistTitle: "Der Pro-Warteliste beitreten?",
    profileGetProTitle: "Pro erhalten",
    profileGetProDescription: "Trage dich in die Mailingliste ein, um Neuigkeiten zum Pro-Start und zum frühen Zugang zu erhalten.",
    profileGetProAction: "Pro erhalten",
    proWaitlistConfirmation: "Wir nehmen {email} in die Mailingliste für Neuigkeiten zum Pro-Start auf. Möchtest du beitreten?",
    proWaitlistCancel: "Nicht jetzt",
    proWaitlistConfirm: "Warteliste beitreten",
    proWaitlistJoining: "Wird hinzugefügt…",
    proWaitlistSuccess: "Du stehst auf der Pro-Warteliste.",
    proWaitlistAlreadyJoined: "Du stehst bereits auf der Pro-Warteliste.",
    proWaitlistError: "Du konntest nicht zur Pro-Warteliste hinzugefügt werden. Bitte versuche es erneut.",
    studioRequestSession: "Session anfragen",
    studioContact: "Kontakt",
    loadingContent: "Inhalte werden geladen",
    profileMedia: "Medien",
    profileGallery: "Galerie",
    profileGalleryImageAlt: "Galeriebild {number}",
    profileNoMedia: "Noch keine Medien.",
    profileSimilarStudios: "Ähnliche Studios",
    profileSimilarMedia: "Ähnliche Medienanbieter",
    profileViewStudio: "Studio ansehen",
    profileViewMedia: "Medienprofil ansehen",
    profileStudioHeroAlt: "Studio {name}",
    profileMediaHeroAlt: "Medienprofil {name}",
    profileRatingLabel: "Mitgliedsbewertung",
    profileAccountLinked: "Konto verknüpft",
    profileRatingReviewOne: "{count} Bewertung",
    profileRatingReviewOther: "{count} Bewertungen",
    profileRatingAccessible: "Mitgliedsbewertung {rating} von 5, {count} Bewertungen",
    profileRatingAccessibleNoCount: "Mitgliedsbewertung {rating} von 5",
    loginModalTitle: "Anmelden, um fortzufahren",
    loginModalDefaultReason: "Melde dich an, um diese Funktion zu nutzen.",
    loginModalMessageReason: "Melde dich an, um ein Gespräch zu beginnen.",
    loginModalFollowReason: "Melde dich an, um dieser Seite zu folgen.",
    follow: "Folgen",
    unfollow: "Nicht mehr folgen",
    followerCountOne: "{count} Follower",
    followerCountOther: "{count} Follower",
    managedPageViewFollowers: "Follower anzeigen",
    managedPageFollowersProRequired: "Mit Pro siehst du, wer dieser Seite folgt.",
    managedPageFollowerAnonymous: "Band-Joe-Mitglied",
    managedPageFollowersEmpty: "Noch keine Follower.",
    managedPageFollowersError: "Follower konnten nicht geladen werden. Bitte versuche es erneut.",
    followAdded: "Zur Liste der gefolgten Seiten hinzugefügt.",
    followRemoved: "Aus der Liste der gefolgten Seiten entfernt.",
    followError: "Die Liste konnte nicht aktualisiert werden. Bitte versuche es erneut.",
    followingTitle: "Gefolgte Seiten",
    followingDescription: "Künstler, Veranstaltungsorte, Studios und Medienschaffende, denen du folgst.",
    followingEmpty: "Du folgst noch keinen Seiten.",
    followTypeartist: "Künstler",
    followTypevenue: "Veranstaltungsort",
    followTypestudio: "Studio",
    followTypemedia: "Medien",
    loginModalInviteReason: "Melde dich an, um eine Einladung zu senden.",
    loginModalReviewReason: "Melde dich an, um eine Bewertung abzugeben.",
    reviewArtistTitle: "Diesen Künstler bewerten",
    reviewVenueTitle: "Diesen Veranstaltungsort bewerten",
    reviewNoEligibleEvent: "Du kannst nach einer gemeinsamen Veranstaltung bewerten. Es gibt keine berechtigten Veranstaltungen.",
    reviewEventLabel: "Abgeschlossene Veranstaltung",
    reviewCategoryprofessionalism: "Professionalität",
    reviewCategorypunctuality: "Pünktlichkeit",
    reviewCategorysound: "Klang",
    reviewCategorycrowd: "Publikumserlebnis",
    reviewTextLabel: "Deine Bewertung",
    reviewCancel: "Abbrechen",
    reviewSubmit: "Bewertung veröffentlichen",
    reviewSubmitting: "Wird veröffentlicht…",
    reviewSuccess: "Deine Bewertung wurde veröffentlicht.",
    eventRateArtist: "Bewerten",
    authEmail: "E-Mail",
    authPassword: "Passwort",
    authTogglePassword: "Passwort ein- oder ausblenden",
    authForgotPassword: "Passwort vergessen?",
    authSignIn: "Anmelden",
    authLogout: "Abmelden",
    inbox: "Posteingang",
    profile: "Profil",
    accountRoleARTIST: "Künstler",
    accountRoleGUEST: "Gast",
    accountRoleVENUE: "Veranstaltungsort",
    accountRoleSTUDIO: "Studio",
    accountRoleMEDIA: "Media",
    accountRoleADMIN: "Administrator",
    signupGuestDescription: "Folge Künstlern, Veranstaltungsorten und Studios, ohne ein Profil zu erstellen oder zu verwalten.",
    authSigningIn: "Anmeldung läuft…",
    authNewHere: "Neu hier?",
    authCreateAccount: "Konto erstellen",
    authBrowseWithoutSigningIn: "Ohne Anmeldung weitersuchen",
    authSignInFailed: "Anmeldung fehlgeschlagen. Bitte versuche es erneut.",
    activationTitle: "Konto aktivieren",
    activationWorking: "Dein Konto wird aktiviert…",
    activationSuccess: "Dein Konto ist aktiv und du bist angemeldet.",
    activationInvalid: "Dieser Aktivierungslink ist ungültig oder abgelaufen.",
    activationContinue: "Weiter zu deinem Profil",
    activationPending: "Konto erstellt. Öffne vor der Anmeldung den Aktivierungslink in deiner E-Mail.",
    profileArtistPagesTitle: "Deine Künstlerprofile",
    profileArtistPagesDescription: "Richte deine Künstlerprofile ein und verwalte sie.",
    profileCreateArtistPage: "Künstlerprofil erstellen",
    profileVenuePagesTitle: "Deine Veranstaltungsortprofile",
    profileVenuePagesDescription: "Richte deine Veranstaltungsortprofile ein und verwalte sie.",
    profileCreateVenuePage: "Veranstaltungsortprofil erstellen",
    profileStudioPagesTitle: "Deine Studioprofile",
    profileStudioPagesDescription: "Richte deine Studioprofile ein und verwalte sie.",
    profileCreateStudioPage: "Studioprofil erstellen",
    profileMediaPagesTitle: "Deine Medienprofile",
    profileMediaPagesDescription: "Richte deine Medienprofile ein und verwalte sie.",
    profileCreateMediaPage: "Medienprofil erstellen",
    profileProLimitSummary: "Mit einem Pro-Konto kannst du bis zu {count} Profile erstellen.",
    profileFreeLimitSummary: "Mit einem kostenlosen Konto kannst du ein Profil erstellen. Mit Pro sind bis zu 5 möglich.",
    profileProLimitReached: "Du hast das Pro-Limit von {count} Profilen erreicht.",
    profileFreeLimitReached: "Du benötigst ein Pro-Konto, um mehr als ein Profil zu erstellen.",
    createPageKindArtist: "KÃ¼nstlerprofil",
    createPageKindVenue: "Veranstaltungsortprofil",
    createPageKindStudio: "Studioprofil",
    createPageKindMedia: "Medienprofil",
    createPageTitle: "{type} erstellen",
    createPageEyebrow: "Neues verwaltetes Profil",
    createPageName: "Anzeigename",
    createPageCity: "Stadt",
    createPageAbout: "Ãœber",
    createPageOnboarding: "Dein Konto ist bereit. Vervollständige diesen Entwurf, damit andere dein Profil finden und verstehen können.",
    createPageMedia: "Bilder",
    createPageProfileImage: "Profilbild",
    createPageCoverImage: "Titelbild",
    createPageGallery: "Galeriebilder",
    imageInputHint: "Lade ein Bild hoch oder gib eine HTTP(S)-URL ein. Uploads werden auf maximal 800 × 800 px vor einem weißen Hintergrund verkleinert.",
    imageInputUpload: "Hochladen",
    imageInputAddUrl: "URL hinzufügen",
    imageInputInvalid: "Gib eine gültige HTTP- oder HTTPS-Bild-URL ein.",
    imageInputInvalidFile: "Wähle ein gültiges PNG-, JPEG- oder WebP-Bild.",
    imageInputPreview: "Bildvorschau {number}",
    imageInputRemove: "Bild {number} entfernen",
    createPageTagEntryHint: "Wert eingeben und Enter drÃ¼cken.",
    createPageContact: "Kontakt und Links",
    createPagePhone: "Öffentliche Telefonnummer",
    createPageWebsite: "Website-URL",
    createPageWebsiteHint: "Du kannst eine vollständige URL oder eine Domain wie bandname.com eingeben.",
    createPageWebsiteInvalid: "Gib eine gültige Website-Adresse mit HTTP oder HTTPS ein.",
    createPageSocialTitle: "Social-Media-Links",
    createPageSocialHint: "Füge die öffentlichen Profile hinzu, die auf deiner Künstlerseite angezeigt werden.",
    createPageSocialInvalid: "Gib gültige Social-Media-Adressen mit HTTP oder HTTPS ein.",
    createPageSocialSpotify: "Spotify-URL",
    createPageSocialYoutube: "YouTube-URL",
    createPageSocialInstagram: "Instagram-URL",
    createPageSocialFacebook: "Facebook-URL",
    createPageSocialSoundcloud: "SoundCloud-URL",
    createPageArtistDetails: "Künstlerdetails",
    createPageVenueDetails: "Details zum Veranstaltungsort",
    createPageStudioDetails: "Studiodetails",
    createPageGenres: "Genres",
    createPageMembers: "Mitglieder",
    createPageTravelDistance: "Reiseentfernung (km)",
    createPageFeeMin: "Mindestgage",
    createPageFeeMax: "Höchstgage",
    createPageAddress: "Adresse",
    createPageCapacity: "Kapazität",
    createPageBudgetMin: "Mindestbudget",
    createPageBudgetMax: "Höchstbudget",
    createPageFacilities: "Ausstattung",
    createPageStageSpec: "Bühnenspezifikation",
    createPagePolicies: "Buchungsrichtlinien",
    createPageServices: "Dienstleistungen",
    createPageRooms: "Räume",
    createPageGear: "Equipment",
    createPageCurrency: "Währung (ISO 4217)",
    createPageCurrencyInvalid: "Gib einen dreistelligen ISO-4217-Währungscode ein.",
    createPageTimeZone: "Zeitzone des Studios",
    createPageTimeZoneHint: "IANA-Name, zum Beispiel Europe/Berlin",
    createPageTimeZoneInvalid: "Gib eine gültige IANA-Zeitzone ein.",
    createPageAddRoom: "Raum hinzufügen",
    createPageRoomsEmpty: "Noch keine Räume hinzugefügt.",
    createPageRoomName: "Raumname",
    createPageRoomSize: "Raumgröße oder Kapazität",
    createPageRoomPhoto: "Raumfoto",
    createPageEngineers: "Tontechniker",
    createPageAddEngineer: "Tontechniker hinzufügen",
    createPageEngineersEmpty: "Noch keine Tontechniker hinzugefügt.",
    createPageEngineerName: "Name",
    createPageEngineerRole: "Rolle oder Spezialgebiet",
    createPageEngineerAvatar: "Profilbild",
    createPageEngineerInstagram: "Instagram-URL",
    createPageRates: "Preise",
    createPageAddRate: "Preis hinzufügen",
    createPageRatesEmpty: "Noch keine Preise hinzugefügt.",
    createPageRateLabel: "Bezeichnung und Einheit",
    createPageRatePrice: "Preis",
    createPagePortfolio: "Portfolio",
    createPageAddPortfolio: "Portfolioelement hinzufügen",
    createPagePortfolioEmpty: "Noch keine Portfolioelemente hinzugefügt.",
    createPagePortfolioType: "Medientyp",
    createPagePortfolioAudio: "Audio",
    createPagePortfolioVideo: "Video",
    createPagePortfolioTitle: "Titel",
    createPagePortfolioUrl: "Medien-URL",
    createPagePortfolioThumb: "Coverbild",
    createPageRemoveItem: "Entfernen",
    availabilityTitle: "Verfügbarkeit",
    availabilityEditorTitle: "Verfügbarkeit veröffentlichen",
    availabilityEditorHint: "Füge buchbare Termine hinzu. Kalendertage bleiben mit der angegebenen IANA-Zeitzone verknüpft.",
    availabilityStartDate: "Startdatum",
    availabilityEndDate: "Enddatum",
    availabilityState: "Status",
    availabilityStateAVAILABLE: "Verfügbar",
    availabilityStateTENTATIVE: "Vorläufig",
    availabilityStateUNAVAILABLE: "Nicht verfügbar",
    availabilityTimeZone: "Zeitzone",
    availabilityTimeZoneHint: "IANA-Name, zum Beispiel Europe/Berlin",
    availabilityNotes: "Öffentliche Hinweise (optional)",
    availabilityAdd: "Termine hinzufügen",
    availabilityRemove: "Verfügbarkeit entfernen",
    availabilityEmpty: "Keine Verfügbarkeit veröffentlicht.",
    availabilityInvalid: "Prüfe, ob jeder Zeitraum gültige Daten, einen Status und eine IANA-Zeitzone enthält.",
    createPageSave: "Entwurf speichern",
    createPageSaving: "Speichern",
    createPageCancel: "Abbrechen",
    editPageTitle: "{type} bearbeiten",
    editPageEyebrow: "Verwaltetes Profil",
    editPageSave: "Änderungen speichern",
    editPageNotOwned: "Diese Seite gehört nicht zu deinen verwalteten Profilen.",
    editPageLocationTitle: "Standort präzisieren",
    editPageLocationHint: "Klicke auf die Karte oder ziehe die Markierung, um den genauen öffentlichen Standort festzulegen.",
    editPageLocationMapLabel: "Profilstandort präzisieren",
    editPageLatitude: "Breitengrad",
    editPageLongitude: "Längengrad",
    editPageLocationInvalid: "Gib gültige Breiten- und Längengradkoordinaten ein.",
    managedPageOpen: "Öffnen",
    managedPageEdit: "Bearbeiten",
    managedPageDelete: "Löschen",
    managedPageDeleteTitle: "Profilseite löschen?",
    managedPageDeleteConfirmation: "„{name}“ dauerhaft löschen? Dadurch werden die Seite und zugehörige Follows, Bewertungen, Unterhaltungen und Ausschreibungen entfernt. Dies kann nicht rückgängig gemacht werden.",
    managedPageDeleteCancel: "Seite behalten",
    managedPageDeleteConfirm: "Seite löschen",
    managedPageDeleting: "Wird gelöscht…",
    managedPageDeleteSuccess: "Profilseite gelöscht.",
    managedPageDeleteError: "Die Profilseite konnte nicht gelöscht werden. Bitte versuche es erneut.",
    artistProfileEdit: "Profil bearbeiten",
    artistProfileMembers: "Mitglieder",
    artistUpcomingEvents: "Kommende Veranstaltungen",
    artistNoUpcomingEvents: "Keine kommenden Veranstaltungen.",
    artistPastEvents: "Vergangene Veranstaltungen",
    artistNoPastEvents: "Noch keine vergangenen Veranstaltungen.",
    venueEventsPostGig: "Gig veröffentlichen",
    venueUpcomingEvents: "Kommende Veranstaltungen",
    venueNoUpcomingEvents: "Keine kommenden Veranstaltungen.",
    venuePastEvents: "Vergangene Veranstaltungen",
    venueNoPastEvents: "Noch keine vergangenen Veranstaltungen.",
    venueEventCompleted: "Beendet",
    venueSetAvailability: "Verfügbarkeit festlegen",
    venueManageAvailability: "Verfügbarkeit verwalten",
    gigPublishSuccess: "Der Gig wurde veröffentlicht und die Veranstaltung erstellt.",
    gigPublishIncomplete: "Der Gig konnte nicht veröffentlicht werden, weil die Veranstaltung nicht erstellt wurde. Bitte versuche es erneut.",
    gigEditTitle: "Gig und Veranstaltung bearbeiten",
    gigEditHeading: "Ausschreibung aktualisieren",
    gigCreateHeading: "Den passenden Act finden",
    gigVenueWorkspace: "Veranstaltungsort-Arbeitsbereich",
    gigBriefTitle: "Ein klares Briefing bringt bessere Bewerbungen",
    gigBriefHint: "Gib Auftrittszeit, Load-in, Backline, Zahlungszeitpunkt und eine realistische Frist an.",
    gigTitle: "Gig-Titel",
    gigDescription: "Beschreibung der Veranstaltung und des Gigs",
    eventBannerUrlLabel: "Bannerbild (optional)",
    eventBannerUrlHint: "Lade das Veranstaltungsbanner hoch oder verlinke es.",
    eventBannerUrlInvalid: "Lade ein Bild hoch oder gib eine gültige HTTP- oder HTTPS-Banner-URL ein.",
    eventParkingLabel: "Parkhinweise (optional)",
    eventAccessibilityLabel: "Barrierefreiheit (optional)",
    eventAccessibilityHint: "FÃ¼ge jedes Merkmal einzeln hinzu.",
    eventArtistInviteLabel: "KÃ¼nstler einladen (optional)",
    eventArtistInviteHint: "Suche einen oder mehrere KÃ¼nstler. Sie werden eingeladen, sobald die Veranstaltung erstellt wird.",
    eventArtistInviteEmpty: "Keine KÃ¼nstler gefunden",
    eventArtistInviteLoadError: "KÃ¼nstler konnten nicht geladen werden. Du kannst die Veranstaltung trotzdem ohne Einladungen erstellen.",
    eventCapacityLabel: "VeranstaltungskapazitÃ¤t",
    eventCapacityInvalid: "Gib eine VeranstaltungskapazitÃ¤t von mindestens 1 ein.",
    eventCapacityExceedsVenue: "Die VeranstaltungskapazitÃ¤t darf die KapazitÃ¤t des Veranstaltungsorts von {capacity} nicht Ã¼berschreiten.",
    eventCapacityVenueMaximum: "Maximum fÃ¼r diesen Veranstaltungsort: {capacity}.",
    eventCapacityVenueUnknown: "Der Veranstaltungsort benÃ¶tigt eine KapazitÃ¤t, bevor eine Veranstaltung gespeichert werden kann.",
    gigPerformanceStart: "Datum und Uhrzeit des Auftritts",
    gigApplicationDeadline: "Bewerbungsfrist",
    gigDoorsAt: "Einlass (optional)",
    gigEndAt: "Ende des Auftritts (optional)",
    gigGenres: "Genres",
    gigBudgetMin: "Mindestbudget",
    gigBudgetMax: "Höchstbudget",
    gigCurrency: "Währung",
    gigSetLength: "Set-Länge (Minuten)",
    gigBackline: "Bereitgestellte Backline",
    gigEquipment: "Ausrüstungsanforderungen",
    gigPaymentTerms: "Zahlungs- und Auftrittsbedingungen",
    gigAgeLimit: "Altersgrenze (optional)",
    gigAttachments: "Anhänge hinzufügen (optional)",
    gigBudgetInvalid: "Das Mindestbudget darf das Höchstbudget nicht überschreiten.",
    gigDeadlineInvalid: "Die Bewerbungsfrist muss vor dem Auftritt liegen.",
    eventStartInvalid: "Gib ein gültiges Startdatum und eine gültige Startzeit ein.",
    eventEndInvalid: "Gib ein gültiges Enddatum und eine gültige Endzeit ein.",
    eventEndBeforeStart: "Das Ende des Auftritts muss nach dem Beginn liegen.",
    eventDoorsInvalid: "Gib ein gültiges Datum und eine gültige Uhrzeit für den Einlass ein.",
    eventDoorsAfterStart: "Der Einlass muss vor oder zum Beginn des Auftritts sein.",
    eventDeadlineInvalid: "Gib eine gültige Bewerbungsfrist ein.",
    gigAttachmentInvalid: "Anhänge müssen PDF-, PNG- oder JPEG-Dateien mit höchstens 10 MB sein.",
    gigSaving: "Wird gespeichert…",
    gigSaveChanges: "Änderungen speichern",
    gigUpdateSuccess: "Gig und Veranstaltung wurden aktualisiert.",
    eventEdit: "Veranstaltung bearbeiten",
    venueInviteArtists: "K\u00fcnstler einladen",
    eventBuyTickets: "Tickets kaufen",
    eventViewOnFacebook: "Auf Facebook ansehen",
    eventGetTickets: "Tickets erhalten",
    eventPriceFree: "Kostenlos",
    eventPriceUnavailable: "Preis nicht verfügbar",
    cityFieldLabel: "Stadt",
    cityFieldPrompt: "Stadt auswÃ¤hlen",
    cityFieldLoading: "StÃ¤dte werden geladenâ€¦",
    cityFieldError: "StÃ¤dte konnten nicht geladen werden. Bitte versuche es erneut.",
    cityFieldSearchWorldwide: "Weltweit suchen",
    cityFieldAttribution: "Standortdaten Â© OpenStreetMap-Mitwirkende",
    discoveryMapLabel: "Veranstaltungsorte",
    venueMapLabel: "Standorte der Veranstaltungsstätten",
    studioMapLabel: "Studiostandorte",
    artistMapLabel: "Standorte von KÃ¼nstlern",
    mediaMapLabel: "Medienstandorte",
    gigMapLabel: "Gig-Standorte",
    filters: "Filter",
    listView: "Liste",
    mapView: "Karte",
    discoveryMapEmpty: "Für diese Ergebnisse sind keine genauen Standorte verfügbar.",
    close: "Schließen",
    inboxConversationError: "Die Unterhaltung konnte nicht geöffnet werden. Bitte versuche es erneut.",
    inboxMessagesError: "Nachrichten konnten nicht geladen werden. Bitte versuche es erneut.",
    inboxSearch: "Unterhaltungen durchsuchen",
    inboxRefreshMessages: "Nachrichten aktualisieren",
    inboxBackToConversations: "Zurück zu Unterhaltungen",
    inboxNoMessages: "Noch keine Nachrichten",
    inboxOrganisationConversation: "Organisationsunterhaltung",
    inboxWriteMessage: "Nachricht schreiben",
    inboxSendMessage: "Nachricht senden",
    inboxRecipientCannotReply: "Dieser Empfänger hat kein Pro-Konto und kann daher hier nicht antworten. Gib in deiner Nachricht deine Kontaktdaten an, damit eine Antwort auf anderem Weg möglich ist.",
    inboxEventInvitationTitle: "Veranstaltungseinladung",
    inboxEventInvitationAccept: "Annehmen",
    inboxEventInvitationDecline: "Ablehnen",
    inboxEventInvitationAccepted: "Einladung angenommen",
    inboxEventInvitationDeclined: "Einladung abgelehnt",
    inboxEventInvitationResponseError: "Die Antwort auf die Einladung konnte nicht gespeichert werden. Bitte versuche es erneut.",
    eventTicketProviderLabel: "Ticketverkaufslink",
    eventTicketProviderHint: "Verlinke die Veranstaltungsseite bei deinem externen Ticketanbieter.",
    eventTicketProviderProHint: "Zum Hinzufügen eines externen Ticketverkaufslinks ist ein Pro-Konto erforderlich.",
  },
  nb: {
    eventViewOnFacebook: "Se pÃ¥ Facebook",
    eventDateAll: "Alle",
    eventDateToday: "I dag",
    eventDateWeekend: "Helg",
    eventDateMonth: "Måned",
    headerSlogan: "Kobler band med enhver Joe",
    developedBy: "Utviklet av",
    appVersion: "Versjon {version}",
    loadingContent: "Laster innhold",
    inbox: "Innboks",
    profileAccountLinked: "Konto tilknyttet",
  },
  pt: {
    eventViewOnFacebook: "Ver no Facebook",
    eventDateAll: "Todos",
    eventDateToday: "Hoje",
    eventDateWeekend: "Fim de semana",
    eventDateMonth: "Mês",
    headerSlogan: "A ligar bandas a cada Joe",
    developedBy: "Desenvolvido por",
    appVersion: "Versão {version}",
    loadingContent: "A carregar conteúdo",
    inbox: "Caixa de entrada",
    profileAccountLinked: "Conta associada",
  },
  ja: {
    eventViewOnFacebook: "Facebookã§è¦‹ã‚‹",
    eventDateAll: "すべて",
    eventDateToday: "今日",
    eventDateWeekend: "週末",
    eventDateMonth: "今月",
    headerSlogan: "バンドとすべてのJoeをつなぐ",
    loadingContent: "コンテンツを読み込んでいます",
    developedBy: "開発元",
    appVersion: "バージョン {version}",
    inbox: "受信トレイ",
    profileAccountLinked: "アカウント連携済み",
  },
  ar: {
    signupRetry: "تعذّر إنشاء الحساب. يُرجى المحاولة لاحقًا.",
    signupPreparing: "جارٍ تجهيز التسجيل…",
    signupWebsite: "الموقع الإلكتروني",
    eventDateAll: "الكل",
    eventDateToday: "اليوم",
    eventDateWeekend: "عطلة نهاية الأسبوع",
    eventDateMonth: "الشهر",
    profileAccountLinked: "الحساب مرتبط",
    headerSlogan: "نربط الفرق الموسيقية بكل Joe",
    homeSearchPlaceholder: "ابحث عن الفعاليات والفنانين والأماكن والاستوديوهات ومصوري الفيديو",
    homeMediaTab: "الوسائط",
    homeMediaTitle: "الوسائط",
    homeMediaSubtitle: "اعثر على وسائل الإعلام للعروض الحية ومقاطع الفيديو الموسيقية والمحتوى الترويجي",
    studioRateFromPerHour: "ابتداءً من {price}/ساعة",
    studioRateOnRequest: "تواصل لمعرفة الأسعار",
    proUpgrade: "الترقية إلى Pro",
    proWaitlistTitle: "هل تريد الانضمام إلى قائمة انتظار Pro؟",
    profileGetProTitle: "احصل على Pro",
    profileGetProDescription: "انضم إلى القائمة البريدية للحصول على أخبار إطلاق Pro والوصول المبكر.",
    profileGetProAction: "احصل على Pro",
    proWaitlistConfirmation: "سنضيف {email} إلى القائمة البريدية لتحديثات إطلاق Pro. هل تريد الانضمام؟",
    proWaitlistCancel: "ليس الآن",
    proWaitlistConfirm: "الانضمام إلى قائمة الانتظار",
    proWaitlistJoining: "جارٍ الانضمام…",
    proWaitlistSuccess: "أنت الآن على قائمة انتظار Pro.",
    proWaitlistAlreadyJoined: "أنت بالفعل على قائمة انتظار Pro.",
    proWaitlistError: "تعذرت إضافتك إلى قائمة انتظار Pro. حاول مرة أخرى.",
    profileMedia: "الوسائط",
    profileGallery: "المعرض",
    profileGalleryImageAlt: "صورة المعرض {number}",
    profileNoMedia: "لا توجد وسائط بعد.",
    profileSimilarStudios: "استوديوهات مشابهة",
    profileSimilarMedia: "جهات إعلامية مشابهة",
    profileViewStudio: "عرض الاستوديو",
    profileViewMedia: "عرض ملف الوسائط",
    profileStudioHeroAlt: "استوديو {name}",
    profileMediaHeroAlt: "ملف الوسائط {name}",
    studioRequestSession: "طلب جلسة",
    studioContact: "تواصل",
    availabilityTitle: "التوفر",
    availabilityEditorTitle: "نشر التوفر",
    availabilityEditorHint: "أضف التواريخ المتاحة للحجز. تظل تواريخ التقويم مرتبطة بالمنطقة الزمنية IANA المحددة.",
    availabilityStartDate: "تاريخ البدء",
    availabilityEndDate: "تاريخ الانتهاء",
    availabilityState: "الحالة",
    availabilityStateAVAILABLE: "متاح",
    availabilityStateTENTATIVE: "مبدئي",
    availabilityStateUNAVAILABLE: "غير متاح",
    availabilityTimeZone: "المنطقة الزمنية",
    availabilityTimeZoneHint: "اسم IANA، مثل Africa/Johannesburg",
    availabilityNotes: "ملاحظات عامة (اختياري)",
    availabilityAdd: "إضافة تواريخ",
    availabilityRemove: "إزالة التوفر",
    availabilityEmpty: "لم يتم نشر أي توفر.",
    availabilityInvalid: "تحقق من أن كل فترة تتضمن تواريخ وحالة ومنطقة زمنية IANA صالحة.",
    profileRatingLabel: "تقييم العضو",
    profileRatingReviewOne: "{count} مراجعة",
    profileRatingReviewOther: "{count} مراجعات",
    profileRatingAccessible: "تقييم العضو {rating} من 5، {count} مراجعات",
    profileRatingAccessibleNoCount: "تقييم العضو {rating} من 5",
    editPageTitle: "تعديل {type}",
    editPageEyebrow: "ملف مُدار",
    editPageSave: "حفظ التغييرات",
    editPageNotOwned: "هذه الصفحة غير متاحة ضمن ملفاتك المُدارة.",
    editPageLocationTitle: "ضبط الموقع بدقة",
    editPageLocationHint: "انقر على الخريطة أو اسحب العلامة لتحديد الموقع العام بدقة.",
    editPageLocationMapLabel: "ضبط موقع الملف بدقة",
    editPageLatitude: "خط العرض",
    editPageLongitude: "خط الطول",
    editPageLocationInvalid: "أدخل إحداثيات صالحة لخط العرض وخط الطول.",
    managedPageOpen: "فتح",
    managedPageEdit: "تعديل",
    managedPageDelete: "حذف",
    managedPageDeleteTitle: "حذف صفحة الملف؟",
    managedPageDeleteConfirmation: "هل تريد حذف «{name}» نهائيًا؟ سيؤدي ذلك إلى إزالة الصفحة والمتابعات والمراجعات والمحادثات والإعلانات المرتبطة بها. لا يمكن التراجع عن هذا الإجراء.",
    managedPageDeleteCancel: "الاحتفاظ بالصفحة",
    managedPageDeleteConfirm: "حذف الصفحة",
    managedPageDeleting: "جارٍ الحذف…",
    managedPageDeleteSuccess: "تم حذف صفحة الملف.",
    managedPageDeleteError: "تعذر حذف صفحة الملف. حاول مرة أخرى.",
    artistProfileEdit: "تعديل الملف الشخصي",
    artistProfileMembers: "أعضاء الفرقة",
    artistUpcomingEvents: "الفعاليات القادمة",
    artistNoUpcomingEvents: "لا توجد فعاليات قادمة.",
    artistPastEvents: "الفعاليات السابقة",
    artistNoPastEvents: "لا توجد فعاليات سابقة حتى الآن.",
    venueEventsPostGig: "نشر حفلة",
    venueUpcomingEvents: "الفعاليات القادمة",
    venueNoUpcomingEvents: "لا توجد فعاليات قادمة.",
    venuePastEvents: "الفعاليات السابقة",
    venueNoPastEvents: "لا توجد فعاليات سابقة حتى الآن.",
    venueEventCompleted: "مكتملة",
    venueSetAvailability: "تحديد التوفر",
    venueManageAvailability: "إدارة التوفر",
    gigPublishSuccess: "تم نشر الحفلة على اللوحة وإنشاء الفعالية.",
    gigPublishIncomplete: "تعذر نشر الحفلة لأن الفعالية لم تُنشأ. يُرجى المحاولة مرة أخرى.",
    gigEditTitle: "تعديل الحفلة والفعالية",
    gigEditHeading: "تحديث الفرصة",
    gigCreateHeading: "ابحث عن العرض المناسب",
    gigVenueWorkspace: "مساحة عمل مكان العرض",
    gigBriefTitle: "التفاصيل الواضحة تجذب طلبات أفضل",
    gigBriefHint: "أضف موعد العرض والتحميل والمعدات وموعد الدفع ومهلة واقعية.",
    gigTitle: "عنوان الحفلة",
    gigDescription: "وصف الفعالية والحفلة",
    gigPerformanceStart: "تاريخ ووقت العرض",
    gigApplicationDeadline: "الموعد النهائي للتقديم",
    gigDoorsAt: "فتح الأبواب (اختياري)",
    gigEndAt: "نهاية العرض (اختياري)",
    gigGenres: "الأنواع الموسيقية",
    gigBudgetMin: "الحد الأدنى للميزانية",
    gigBudgetMax: "الحد الأقصى للميزانية",
    gigCurrency: "العملة",
    gigSetLength: "مدة العرض بالدقائق",
    gigBackline: "المعدات المتوفرة",
    gigEquipment: "متطلبات المعدات",
    gigPaymentTerms: "شروط الدفع والتعاقد",
    gigAgeLimit: "الحد العمري (اختياري)",
    gigAttachments: "إضافة مرفقات (اختياري)",
    gigBudgetInvalid: "يجب ألا يتجاوز الحد الأدنى للميزانية الحد الأقصى.",
    gigDeadlineInvalid: "يجب أن يكون الموعد النهائي للتقديم قبل العرض.",
    eventStartInvalid: "أدخل تاريخ ووقت بدء صالحين.",
    eventEndInvalid: "أدخل تاريخ ووقت نهاية صالحين.",
    eventEndBeforeStart: "يجب أن تكون نهاية العرض بعد بدايته.",
    eventDoorsInvalid: "أدخل تاريخ ووقت فتح الأبواب بشكل صالح.",
    eventDoorsAfterStart: "يجب فتح الأبواب قبل بدء العرض أو عند بدئه.",
    eventDeadlineInvalid: "أدخل موعدًا نهائيًا صالحًا للتقديم.",
    gigAttachmentInvalid: "يجب أن تكون المرفقات بصيغة PDF أو PNG أو JPEG وألا يتجاوز حجمها 10 ميغابايت.",
    gigSaving: "جارٍ الحفظ…",
    gigSaveChanges: "حفظ التغييرات",
    gigUpdateSuccess: "تم تحديث الحفلة والفعالية.",
    eventEdit: "تعديل الفعالية",
    eventBannerUrlLabel: "صورة الغلاف (اختياري)",
    eventBannerUrlHint: "حمّل صورة غلاف الفعالية أو أضف رابطها.",
    eventBannerUrlInvalid: "حمّل صورة أو أدخل رابط HTTP أو HTTPS صالحًا.",
    imageInputHint: "حمّل صورة أو أدخل رابط HTTP(S). يتم تصغير الصور المحمّلة لتلائم 800 × 800 بكسل على خلفية بيضاء.",
    imageInputUpload: "تحميل",
    imageInputAddUrl: "إضافة رابط",
    imageInputInvalid: "أدخل رابط HTTP أو HTTPS صالحًا للصورة.",
    imageInputInvalidFile: "اختر صورة PNG أو JPEG أو WebP صالحة.",
    imageInputPreview: "معاينة الصورة {number}",
    imageInputRemove: "إزالة الصورة {number}",
    eventParkingLabel: "\u062a\u0641\u0627\u0635\u064a\u0644 \u0645\u0648\u0627\u0642\u0641 \u0627\u0644\u0633\u064a\u0627\u0631\u0627\u062a (\u0627\u062e\u062a\u064a\u0627\u0631\u064a)",
    eventAccessibilityLabel: "\u0645\u064a\u0632\u0627\u062a \u0625\u0645\u0643\u0627\u0646\u064a\u0629 \u0627\u0644\u0648\u0635\u0648\u0644 (\u0627\u062e\u062a\u064a\u0627\u0631\u064a)",
    eventAccessibilityHint: "\u0623\u0636\u0641 \u0643\u0644 \u0645\u064a\u0632\u0629 \u0639\u0644\u0649 \u062d\u062f\u0629.",
    eventArtistInviteLabel: "\u062f\u0639\u0648\u0629 \u0641\u0646\u0627\u0646\u064a\u0646 (\u0627\u062e\u062a\u064a\u0627\u0631\u064a)",
    eventArtistInviteHint: "\u0627\u0628\u062d\u062b \u0639\u0646 \u0641\u0646\u0627\u0646 \u0623\u0648 \u0623\u0643\u062b\u0631. \u0633\u062a\u064f\u0631\u0633\u0644 \u0627\u0644\u062f\u0639\u0648\u0627\u062a \u0639\u0646\u062f \u0625\u0646\u0634\u0627\u0621 \u0627\u0644\u0641\u0639\u0627\u0644\u064a\u0629.",
    eventArtistInviteEmpty: "\u0644\u0645 \u064a\u062a\u0645 \u0627\u0644\u0639\u062b\u0648\u0631 \u0639\u0644\u0649 \u0641\u0646\u0627\u0646\u064a\u0646",
    eventArtistInviteLoadError: "\u062a\u0639\u0630\u0631 \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0641\u0646\u0627\u0646\u064a\u0646. \u064a\u0645\u0643\u0646\u0643 \u0625\u0646\u0634\u0627\u0621 \u0627\u0644\u0641\u0639\u0627\u0644\u064a\u0629 \u0628\u062f\u0648\u0646 \u062f\u0639\u0648\u0627\u062a.",
    eventCapacityLabel: "\u0633\u0639\u0629 \u0627\u0644\u0641\u0639\u0627\u0644\u064a\u0629",
    eventCapacityInvalid: "\u0623\u062f\u062e\u0644 \u0633\u0639\u0629 \u0644\u0644\u0641\u0639\u0627\u0644\u064a\u0629 \u0644\u0627 \u062a\u0642\u0644 \u0639\u0646 1.",
    eventCapacityExceedsVenue: "\u0644\u0627 \u064a\u0645\u0643\u0646 \u0623\u0646 \u062a\u062a\u062c\u0627\u0648\u0632 \u0633\u0639\u0629 \u0627\u0644\u0641\u0639\u0627\u0644\u064a\u0629 \u0633\u0639\u0629 \u0627\u0644\u0645\u0643\u0627\u0646 \u0627\u0644\u0628\u0627\u0644\u063a\u0629 {capacity}.",
    eventCapacityVenueMaximum: "\u0627\u0644\u062d\u062f \u0627\u0644\u0623\u0642\u0635\u0649 \u0644\u0647\u0630\u0627 \u0627\u0644\u0645\u0643\u0627\u0646: {capacity}.",
    eventCapacityVenueUnknown: "\u064a\u062c\u0628 \u062a\u062d\u062f\u064a\u062f \u0633\u0639\u0629 \u0627\u0644\u0645\u0643\u0627\u0646 \u0642\u0628\u0644 \u062d\u0641\u0638 \u0627\u0644\u0641\u0639\u0627\u0644\u064a\u0629.",
    venueInviteArtists: "\u062f\u0639\u0648\u0629 \u0641\u0646\u0627\u0646\u064a\u0646",
    eventBuyTickets: "\u0634\u0631\u0627\u0621 \u0627\u0644\u062a\u0630\u0627\u0643\u0631",
    eventViewOnFacebook: "\u0639\u0631\u0636 \u0639\u0644\u0649 Facebook",
    eventGetTickets: "\u0627\u0644\u062d\u0635\u0648\u0644 \u0639\u0644\u0649 \u0627\u0644\u062a\u0630\u0627\u0643\u0631",
    eventPriceFree: "\u0645\u062c\u0627\u0646\u064a",
    eventPriceUnavailable: "\u0627\u0644\u0633\u0639\u0631 \u063a\u064a\u0631 \u0645\u062a\u0627\u062d",
    feeUnavailable: "\u0627\u0644\u0623\u062c\u0631 \u063a\u064a\u0631 \u0645\u062a\u0627\u062d",
    priceRangeFrom: "\u0627\u0628\u062a\u062f\u0627\u0621\u064b \u0645\u0646 {amount}",
    priceRangeUpTo: "\u062d\u062a\u0649 {amount}",
    stageLayoutLabel: "\u0645\u062e\u0637\u0637 \u0627\u0644\u0645\u0633\u0631\u062d",
    stageLayoutDownload: "\u062a\u0646\u0632\u064a\u0644 \u0645\u062e\u0637\u0637 \u0627\u0644\u0645\u0633\u0631\u062d",
    artistBannerAlt: "\u0635\u0648\u0631\u0629 \u063a\u0644\u0627\u0641 {name}",
    accountRoleARTIST: "فنان",
    accountRoleGUEST: "ضيف",
    accountRoleVENUE: "مكان عروض",
    accountRoleSTUDIO: "استوديو",
    accountRoleMEDIA: "وسائط",
    accountRoleADMIN: "مسؤول",
    signupGuestDescription: "تابع الفنانين وأماكن العروض والاستوديوهات من دون إنشاء أو إدارة صفحة.",
    profileArtistPagesTitle: "ملفات الفنان الخاصة بك",
    profileArtistPagesDescription: "قم بإعداد وإدارة ملفات الفنان التي تملكها.",
    profileCreateArtistPage: "إنشاء ملف فنان",
    profileVenuePagesTitle: "ملفات أماكن العروض الخاصة بك",
    profileVenuePagesDescription: "قم بإعداد وإدارة ملفات أماكن العروض التي تملكها.",
    profileCreateVenuePage: "إنشاء ملف مكان عرض",
    profileStudioPagesTitle: "ملفات الاستوديو الخاصة بك",
    profileStudioPagesDescription: "قم بإعداد وإدارة ملفات الاستوديو التي تملكها.",
    profileCreateStudioPage: "إنشاء ملف استوديو",
    profileMediaPagesTitle: "ملفات الوسائط الخاصة بك",
    profileMediaPagesDescription: "قم بإعداد وإدارة ملفات وسائل الإعلام التي تملكها.",
    profileCreateMediaPage: "إنشاء ملف وسائط",
    profileProLimitSummary: "يمكن لحسابات Pro إنشاء ما يصل إلى {count} ملفات.",
    profileFreeLimitSummary: "يمكن للحسابات المجانية إنشاء ملف واحد. قم بالترقية إلى Pro لإنشاء ما يصل إلى 5 ملفات.",
    profileProLimitReached: "لقد وصلت إلى حد Pro البالغ {count} ملفات.",
    profileFreeLimitReached: "تحتاج إلى حساب Pro لإنشاء أكثر من ملف واحد.",
    authLogout: "تسجيل الخروج",
    inbox: "صندوق الوارد",
    profile: "الملف الشخصي",
    loginModalTitle: "سجّل الدخول للمتابعة",
    loginModalDefaultReason: "سجّل الدخول لاستخدام هذه الميزة.",
    loginModalMessageReason: "سجّل الدخول لبدء محادثة.",
    loginModalFollowReason: "سجّل الدخول لمتابعة هذه الصفحة.",
    follow: "متابعة",
    unfollow: "إلغاء المتابعة",
    followerCountOne: "متابع واحد",
    followerCountOther: "{count} متابع",
    managedPageViewFollowers: "عرض المتابعين",
    managedPageFollowersProRequired: "قم بالترقية إلى Pro لمعرفة من يتابع هذه الصفحة.",
    managedPageFollowerAnonymous: "عضو Band-Joe",
    managedPageFollowersEmpty: "لا يوجد متابعون بعد.",
    managedPageFollowersError: "تعذر تحميل المتابعين. يرجى المحاولة مرة أخرى.",
    followAdded: "تمت الإضافة إلى قائمة المتابعة.",
    followRemoved: "تمت الإزالة من قائمة المتابعة.",
    followError: "تعذر تحديث قائمة المتابعة. حاول مرة أخرى.",
    followingTitle: "المتابَعون",
    followingDescription: "الفنانون وأماكن العروض والاستوديوهات وصنّاع المحتوى الذين تتابعهم.",
    followingEmpty: "لا تتابع أي صفحات بعد.",
    followTypeartist: "فنان",
    followTypevenue: "مكان عروض",
    followTypestudio: "استوديو",
    followTypemedia: "إعلام",
    loginModalInviteReason: "سجّل الدخول لإرسال دعوة.",
    loginModalReviewReason: "سجّل الدخول لترك مراجعة.",
    reviewArtistTitle: "قيّم هذا الفنان",
    reviewVenueTitle: "قيّم هذا المكان",
    reviewNoEligibleEvent: "يمكنك كتابة مراجعة بعد انتهاء فعالية مشتركة. لا توجد فعاليات مؤهلة للمراجعة.",
    reviewEventLabel: "الفعالية المكتملة",
    reviewCategoryprofessionalism: "الاحترافية",
    reviewCategorypunctuality: "الالتزام بالوقت",
    reviewCategorysound: "الصوت",
    reviewCategorycrowd: "تجربة الجمهور",
    reviewTextLabel: "مراجعتك",
    reviewCancel: "إلغاء",
    reviewSubmit: "نشر المراجعة",
    reviewSubmitting: "جارٍ النشر…",
    reviewSuccess: "تم نشر مراجعتك.",
    eventRateArtist: "قيّم",
    authEmail: "البريد الإلكتروني",
    authPassword: "كلمة المرور",
    authTogglePassword: "إظهار كلمة المرور أو إخفاؤها",
    authForgotPassword: "هل نسيت كلمة المرور؟",
    authSignIn: "تسجيل الدخول",
    authSigningIn: "جارٍ تسجيل الدخول…",
    authNewHere: "مستخدم جديد؟",
    authCreateAccount: "إنشاء حساب",
    authBrowseWithoutSigningIn: "تصفح بدون تسجيل الدخول",
    authSignInFailed: "تعذّر تسجيل الدخول. حاول مرة أخرى.",
    activationTitle: "تفعيل حسابك",
    activationWorking: "جارٍ تفعيل حسابك…",
    activationSuccess: "تم تفعيل حسابك وتسجيل دخولك.",
    activationInvalid: "رابط التفعيل هذا غير صالح أو انتهت صلاحيته.",
    activationContinue: "المتابعة إلى ملفك الشخصي",
    activationPending: "تم إنشاء الحساب. افتح رابط التفعيل في بريدك الإلكتروني قبل تسجيل الدخول.",
    close: "إغلاق",
    inboxConversationError: "تعذّر فتح المحادثة. يُرجى المحاولة مرة أخرى.",
    inboxMessagesError: "تعذّر تحميل الرسائل. يُرجى المحاولة مرة أخرى.",
    inboxSearch: "البحث في المحادثات",
    inboxBackToConversations: "العودة إلى المحادثات",
    inboxNoMessages: "لا توجد رسائل بعد",
    inboxOrganisationConversation: "محادثة المؤسسة",
    inboxWriteMessage: "اكتب رسالة",
    inboxSendMessage: "إرسال الرسالة",
    inboxRecipientCannotReply: "لا يملك هذا المستلم حساب Pro، لذلك لن يتمكن من الرد هنا. أدرج بيانات الاتصال الخاصة بك في الرسالة ليتمكن من الرد بطريقة أخرى.",
    inboxEventInvitationTitle: "دعوة لفعالية",
    inboxEventInvitationAccept: "قبول",
    inboxEventInvitationDecline: "رفض",
    inboxEventInvitationAccepted: "تم قبول الدعوة",
    inboxEventInvitationDeclined: "تم رفض الدعوة",
    inboxEventInvitationResponseError: "تعذر حفظ الرد على الدعوة. حاول مرة أخرى.",
    eventTicketProviderLabel: "رابط بيع التذاكر",
    eventTicketProviderHint: "أضف رابط صفحة الفعالية لدى مزود التذاكر الخارجي.",
    eventTicketProviderProHint: "يلزم حساب Pro لإضافة رابط خارجي لبيع التذاكر.",
    loadingContent: "جارٍ تحميل المحتوى",
    developedBy: "طُوّر بواسطة",
    appVersion: "الإصدار {version}",
  },
};

function repairDisplayEncoding(value) {
  if (typeof value !== "string") return value;
  return value
    .replace(/\u00c3\u00a2\u00e2\u201a\u00ac\u00c2\u00a6/g, "\u2026")
    .replace(/\u00e2\u20ac\u00a6/g, "\u2026")
    .replace(/\u00c3\u201a\u00c2\u00a9/g, "\u00a9")
    .replace(/\u00c2\u00a9/g, "\u00a9")
    .replace(/\u00c3\u0192\u00c2\u00bc/g, "\u00fc")
    .replace(/\u00c3\u0192\u00c2\u00a4/g, "\u00e4")
    .replace(/\u00c3\u0192\u00c5\u201c/g, "\u00dc")
    .replace(/\u00c3\u00bc/g, "\u00fc")
    .replace(/\u00c3\u00a4/g, "\u00e4")
    .replace(/\u00c3\u0153/g, "\u00dc");
}

export function getMessage(locale, key) {
  const language = String(locale || "en").split("-")[0].toLowerCase();
  return repairDisplayEncoding(MESSAGES[language]?.[key] || MESSAGES.en[key] || key);
}

export function LocaleProvider({ children, region = "Cape Town" }) {
  const [preferences, setPreferences] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("bandjoe_preferences")) || {};
    } catch {
      return {};
    }
  });

  const market = MARKET_BY_REGION[region] || FALLBACK_MARKET;
  const value = useMemo(() => ({
    region,
    country: preferences.country || market.country,
    locale: preferences.locale || market.locale,
    currency: preferences.currency || market.currency,
    timeZone: preferences.timeZone || market.timeZone,
    direction: preferences.direction || "ltr",
    setPreferences,
  }), [market, preferences, region]);

  useEffect(() => {
    localStorage.setItem("bandjoe_preferences", JSON.stringify(preferences));
    document.documentElement.lang = value.locale;
    document.documentElement.dir = value.direction;
  }, [preferences, value.direction, value.locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext) || { ...FALLBACK_MARKET, region: "Cape Town", direction: "ltr" };
}

export function useMessage(key) {
  const { locale } = useLocale();
  return getMessage(locale, key);
}

export function formatMoney(amount, { locale = "en-ZA", currency = "ZAR", maximumFractionDigits = 0 } = {}) {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return "Price unavailable";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits,
  }).format(Number(amount));
}

export function formatEventPrice(event, { locale = "en-ZA", currency = "ZAR" } = {}) {
  const legacyPrice = event?.price;
  const minorPrice = event?.priceMinor;
  const amount = legacyPrice !== null && legacyPrice !== undefined && legacyPrice !== ""
    ? Number(legacyPrice)
    : minorPrice !== null && minorPrice !== undefined && minorPrice !== ""
      ? Number(minorPrice) / 100
      : null;

  if (amount === null || !Number.isFinite(amount)) return getMessage(locale, "eventPriceUnavailable");
  if (amount === 0) return getMessage(locale, "eventPriceFree");
  return formatMoney(amount, { locale, currency: event?.currency || currency });
}

export function hasEventPrice(event) {
  const legacyPrice = event?.price;
  const minorPrice = event?.priceMinor;
  const value = legacyPrice !== null && legacyPrice !== undefined && legacyPrice !== ""
    ? legacyPrice
    : minorPrice !== null && minorPrice !== undefined && minorPrice !== ""
      ? minorPrice
      : null;

  return value !== null && Number.isFinite(Number(value));
}

export function formatFollowerCount(count, locale = "en-ZA") {
  const value = Math.max(0, Number(count) || 0);
  const plural = new Intl.PluralRules(locale).select(value);
  const key = plural === "one" ? "followerCountOne" : "followerCountOther";
  return getMessage(locale, key).replace(
    "{count}",
    new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)
  );
}

export function formatMoneyRange(min, max, context) {
  const normalizeAmount = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
  };
  min = normalizeAmount(min);
  max = normalizeAmount(max);

  if (min === null && max === null) return getMessage(context?.locale || "en-ZA", "feeUnavailable");
  const locale = context?.locale || "en-ZA";
  if (min === null) return getMessage(locale, "priceRangeUpTo").replace("{amount}", formatMoney(max, context));
  if (max === null) return getMessage(locale, "priceRangeFrom").replace("{amount}", formatMoney(min, context));
  if (min === max) return formatMoney(min, context);
  return `${formatMoney(min, context)}–${formatMoney(max, context)}`;
}

function formatDisplayDate(date, { locale = "en-ZA", timeZone = "UTC" } = {}) {
  const parts = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    timeZone,
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value || "";
  return `${part("weekday")} ${part("day")} ${part("month")}`;
}

export function formatTime(value, { locale = "en-ZA", timeZone = "Africa/Johannesburg" } = {}) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(date);
}

export function formatDate(value, context = {}) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatDisplayDate(date, {
    locale: context.locale,
    timeZone: context.timeZone || "Africa/Johannesburg",
  });
}

export function formatDateTime(value, context = {}) {
  if (!value) return "";
  if (Number.isNaN(new Date(value).getTime())) return "";
  return `${formatDate(value, context)} ${formatTime(value, context)}`;
}

export function formatCalendarDate(value, { locale = "en-ZA" } = {}) {
  if (!value) return "";
  const date = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00Z`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatDisplayDate(date, { locale, timeZone: "UTC" });
}
